import { GameServices } from "game/GameServices";
import { View } from "@westpierstudio/luma/mvc";
import { Ease, Tween } from "@westpierstudio/luma/createjs";
import { Signal } from "type-signals";
import { Container, Graphics, Sprite } from "pixi.js";

export enum PanelState {
    DISPLAYING = `DISPLAYING`,
    ANIMATING_IN = `ANIMATING_IN`,
    ANIMATING_OUT = `ANIMATING_OUT`,
    OFFSCREEN = `OFFSCREEN`
}

export class FullScreenPanelSignals {
    animateInComplete = new Signal();
    animateOutComplete = new Signal();
}

export class FullScreenPanel extends View {
    protected background: Sprite;

    protected frameBars: {
        left: Sprite;
        right: Sprite;
        top: Sprite;
        bottom: Sprite;
    };

    protected frameCorners: {
        topLeft: Sprite;
        topRight: Sprite;
        bottomLeft: Sprite;
        bottomRight: Sprite;
    };

    protected offScreenPosition = {x: 0, y: 0};
    protected displayPosition = {x: 0, y: 0};
    protected moveTween?: createjs.Tween;

    protected state: PanelState = PanelState.OFFSCREEN;

    protected container: Container;
    protected maskGraphic: Graphics;

    contentContainer: Container;

    signals = new FullScreenPanelSignals();

    constructor(protected override services: GameServices, protected readonly animateTime: number) {
        super(services);

        const {factory, resources} = this.services;

        const spec = resources.getSpec(`fullscreenpanel`);

        this.container = factory.createContainer({}, this);
        this.background = factory.createSprite(spec.background, this.container);

        this.frameBars = {
            left: factory.createSprite(spec.frameVertical, this.container),
            right: factory.createSprite(spec.frameVertical, this.container),
            top: factory.createSprite(spec.frameHorizontal, this.container),
            bottom: factory.createSprite(spec.frameHorizontal, this.container),
        };

        this.frameCorners = {
            topLeft: factory.createSprite({...spec.frameCorner, scale: {x: 1, y: 1}, anchor: {x: 0, y: 0}}, this.container),
            topRight: factory.createSprite({...spec.frameCorner, scale: {x: -1, y: 1}, anchor: {x: 1, y: 0}}, this.container),
            bottomLeft: factory.createSprite({...spec.frameCorner, scale: {x: 1, y: -1}, anchor: {x: 0, y: 1}}, this.container),
            bottomRight: factory.createSprite({...spec.frameCorner, scale: {x: -1, y: -1}, anchor: {x: 1, y: 1}}, this.container),
        };

        this.maskGraphic = factory.createGraphics({}, this.container);
        this.maskGraphic.beginFill(0x00FF00, 0.5);
        this.maskGraphic.drawRect(0, 0, 1, 1);

        this.contentContainer = factory.createContainer({}, this.container);

        this.services.layout.onResize.add(this.resize, this);
        this.resize();
        this.setToOffScreen();
    }

    protected resize() {
        const bounds = this.services.layout.getScreenBoundaryInBucketSpace();
        const {width, height, bottom, top, left} = bounds;

        // proportionally scale the background, aligning by half the excess to keep it central
        if (width > height) {
            this.background.width = width;
            this.background.scale.y = this.background.scale.x;
            this.background.x = 0;
            this.background.y = (height - this.background.height) / 2;
        } else {
            this.background.height = height;
            this.background.scale.x = this.background.scale.y;
            this.background.x = (width - this.background.width) / 2;
            this.background.y = 0;
        }

        const frameThickness = this.frameCorners.topLeft.width;

        // position the frame bars
        this.frameBars.left.position.set(0, frameThickness);
        this.frameBars.right.position.set(width - this.frameBars.right.width, frameThickness);
        this.frameBars.top.position.set(frameThickness, 0);
        this.frameBars.bottom.position.set(frameThickness, height - this.frameBars.bottom.height);

        const cornerSpace = frameThickness * 2;
        // scale the frame bars to the width/height
        this.frameBars.left.height = height - cornerSpace;
        this.frameBars.right.height = height - cornerSpace;
        this.frameBars.top.width = width - cornerSpace;
        this.frameBars.bottom.width = width - cornerSpace;

        // position the corners
        this.frameCorners.topLeft.position.set(0, 0);
        this.frameCorners.topRight.position.set(width - frameThickness, 0);
        this.frameCorners.bottomLeft.position.set(0, height - frameThickness);
        this.frameCorners.bottomRight.position.set(width - frameThickness, height - frameThickness);

        // mask to the screen size
        this.maskGraphic.width = width;
        this.maskGraphic.height = height;

        this.offScreenPosition.x = left;
        this.offScreenPosition.y = bottom;

        this.displayPosition.x = left;
        this.displayPosition.y = top;

        // position the content container to the bucket 0, 0
        this.contentContainer.position.set(-left, -top);

        if (this.moveTween && this.state === PanelState.ANIMATING_IN) {
            this.animateIn(this.moveTween.position);
        } else if (this.moveTween && this.state === PanelState.ANIMATING_OUT) {
            this.animateOut(this.moveTween.position);
        } else if (this.state === PanelState.DISPLAYING) {
            this.setToDisplay();
        } else if (this.state === PanelState.OFFSCREEN) {
            this.setToOffScreen();
        }
    }

    setToOffScreen() {
        this.clearMask();
        this.visible = false;
        this.container.position.copyFrom(this.offScreenPosition);
        this.state = PanelState.OFFSCREEN;
    }

    setToDisplay() {
        this.setMask();
        this.visible = true;
        this.container.position.copyFrom(this.displayPosition);
        this.state = PanelState.DISPLAYING;
    }

    animateIn(position: number = 0) {

        this.setToOffScreen();

        this.state = PanelState.ANIMATING_IN;
        this.visible = true;
        this.setMask();

        this.moveTween = Tween.get(this.container, {override: true})
            .to(this.displayPosition, this.animateTime, Ease.linear)
            .call(() => {
                this.setToDisplay();
                this.moveTween = undefined;
                this.signals.animateInComplete.dispatch();
            });

        this.moveTween.setPosition(position);
    }

    animateOut(position: number = 0) {

        this.setToDisplay();

        this.state = PanelState.ANIMATING_OUT;
        this.visible = true;
        this.setMask();

        this.moveTween = Tween.get(this.container, {override: true})
            .to(this.offScreenPosition, this.animateTime, Ease.linear)
            .call(() => {
                this.setToOffScreen();
                this.moveTween = undefined;
                this.signals.animateOutComplete.dispatch();
            });

        this.moveTween.setPosition(position);
    }

    override reset(): void {
        this.setToOffScreen();
    }

    protected setMask() {
        this.background.mask = this.maskGraphic;
    }

    protected clearMask() {
        this.background.mask = null;
        this.maskGraphic.renderable = false;
    }
}